import React, { useEffect, useState, useCallback } from 'react';
import {
    Calculator,
    TrendingDown,
    TrendingUp,
    CheckCircle2,
    Clock,
    FileUp,
    Upload,
    Loader2,
    X,
    ShieldCheck
} from 'lucide-react';
import AppendixWrapper from './common/AppendixWrapper';
import { useAppendix } from '../context/AppendixContext';
import { useAuth } from '../context/AuthContext';
import { useSalesData } from '../context/SalesDataContext';

const PriceAppendix = ({
    isOpen,
    onClose,
    loadingImagina,
    loadingComparison,
    imaginaData,
    comparisonData,
    tarifaName,
    cups,
    idTarifa,
    onAnalysisComplete,
    onExtractionComplete,
    fetchImaginaPrice,
    initialAnalysisFile,
    setInitialAnalysisFile
}) => {
    const { authFetch } = useAuth();
    const { updateSectionData } = useSalesData();
    const { registerAppendix, setAppendixStatus } = useAppendix();
    const id = 'price-appendix';



    useEffect(() => {
        registerAppendix(id, {
            title: 'Propuesta Económica',
            icon: Calculator
        });
    }, [registerAppendix]);

    useEffect(() => {
        // Keep appendix open if we have basic data
        const shouldBeOpen = isOpen && (!!cups && !!idTarifa);
        setAppendixStatus(id, shouldBeOpen);
    }, [isOpen, cups, idTarifa, setAppendixStatus]);

    const formatPrice = (val, decimals = 2) => {
        return (val || 0).toLocaleString('es-ES', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }) + '€';
    };

    return (
        <AppendixWrapper 
            id={id} 
            title="Propuesta Económica" 
            icon={Calculator} 
            className="w-[360px]"
            onClose={onClose}
            defaultPosition={{ x: 260, y: 100 }}
        >
            <div className="flex h-full bg-slate-950 overflow-hidden">


                {/* Main Content Area */}
                <div className="flex-1 flex flex-col min-w-0">
                    <div className="px-4 py-2 bg-slate-800/50 border-b border-slate-700">
                        {tarifaName && (
                            <p className="text-[10px] text-primary font-bold truncate">
                                {tarifaName}
                            </p>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar max-h-[500px]">
                        {/* Section 1: Precio Imagina Energía */}
                        <section className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Precio Imagina Energía</h3>
                                {loadingImagina && <Clock size={12} className="text-primary animate-spin" />}
                            </div>

                            {loadingImagina ? (
                                <div className="py-4 flex flex-col items-center justify-center bg-slate-800/30 rounded-lg border border-slate-700/50">
                                    <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-2" />
                                    <span className="text-slate-500 uppercase" style={{ fontSize: '10px' }}>Calculando...</span>
                                </div>
                            ) : imaginaData ? (
                                <div className="grid grid-cols-1 gap-2">
                                    <div className="bg-slate-800/80 p-3 rounded-lg border border-primary/30 flex justify-between items-center shadow-lg shadow-primary/5">
                                        <div className="flex items-center gap-2">
                                            <Calculator size={14} className="text-primary" />
                                            <span className="text-xs text-white font-bold uppercase">Total Anual</span>
                                        </div>
                                        <span className="text-sm font-black text-primary font-mono" id="total-anual-imagina">
                                            {formatPrice((imaginaData.coste_energia || 0) + (imaginaData.coste_potencia || 0), 3)}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-slate-800/40 p-2 rounded border border-slate-700/50 flex flex-col">
                                            <span className="text-[9px] text-slate-500 uppercase font-bold">Energía</span>
                                            <span className="text-xs font-mono text-slate-300">{formatPrice(imaginaData.coste_energia, 3)}</span>
                                        </div>
                                        <div className="bg-slate-800/40 p-2 rounded border border-slate-700/50 flex flex-col">
                                            <span className="text-[9px] text-slate-500 uppercase font-bold">Potencia</span>
                                            <span className="text-xs font-mono text-slate-300">{formatPrice(imaginaData.coste_potencia, 3)}</span>
                                        </div>
                                    </div>

                                    <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50 flex justify-between items-center group hover:border-blue-400/30 transition-colors">
                                        <div className="flex items-center gap-2">
                                            <Clock size={14} className="text-blue-400" />
                                            <span className="text-xs text-slate-300 font-medium italic">Mensual Estimado</span>
                                        </div>
                                        <span className="text-sm font-bold text-blue-200 font-mono" id="total-mensual-imagina">
                                            {formatPrice(((imaginaData.coste_energia || 0) + (imaginaData.coste_potencia || 0)) / 12, 3)}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="py-4 text-center bg-slate-800/20 rounded-lg border border-dashed border-slate-700">
                                    <span className="text-[10px] text-slate-600">Introduzca CUPS para calcular</span>
                                </div>
                            )}
                        </section>

                        {/* Section 2: Comparativa de Precio */}
                        {(loadingComparison || comparisonData) && (
                            <section className="space-y-3 pt-4 border-t border-slate-800 animate-in fade-in duration-500">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Comparativa vs Factura</h3>
                                    {loadingComparison && <Clock size={12} className="text-emerald-500 animate-spin" />}
                                </div>

                                {loadingComparison ? (
                                    <div className="py-4 flex flex-col items-center justify-center bg-emerald-500/5 rounded-lg border border-emerald-500/20">
                                        <div className="w-4 h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mb-2" />
                                        <span className="text-[10px] text-emerald-500 uppercase">Analizando ahorro...</span>
                                    </div>
                                ) : comparisonData ? (
                                    <div className="space-y-2">
                                        {(() => {
                                            const ahorro = comparisonData.ahorro_estimado_mes || 0;
                                            const hasSaving = ahorro > 0;
                                            
                                            return (
                                                <div className={`${hasSaving ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'} p-4 rounded-xl border relative overflow-hidden group`}>
                                                    <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                                                        {hasSaving ? <TrendingDown size={48} className="text-emerald-500" /> : <TrendingUp size={48} className="text-rose-500" />}
                                                    </div>
                                                    <p className={`text-[10px] font-bold ${hasSaving ? 'text-emerald-500/70' : 'text-rose-500/70'} uppercase`}>
                                                        {hasSaving ? 'Ahorro Mensual Estimado' : 'Resultado Propuesta'}
                                                    </p>
                                                    <p className={`text-3xl font-black ${hasSaving ? 'text-emerald-400' : 'text-rose-400'} tracking-tighter mt-1`}>
                                                        {hasSaving ? formatPrice(ahorro, 3) : 'Sin Ahorro'}
                                                    </p>
                                                    <div className="mt-3 flex items-center gap-1.5">
                                                        {hasSaving ? (
                                                            <>
                                                                <CheckCircle2 size={12} className="text-emerald-500" />
                                                                <span className="text-[9px] text-emerald-400/80 font-bold uppercase tracking-tight">Mejora garantizada</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <X size={12} className="text-rose-500" />
                                                                <span className="text-[9px] text-rose-400/80 font-bold uppercase tracking-tight">Sin mejora económica</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        <div className="bg-slate-800/30 p-3 rounded-lg border border-slate-700/50 text-[10px] space-y-2">
                                            <div className="space-y-1">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-400 uppercase tracking-tight font-bold">Total Factura (MENSUAL):</span>
                                                    <span className="text-slate-200 font-mono">{formatPrice(comparisonData.total_factura_mes, 3)}</span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 pl-2 border-l border-slate-700/50">
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-500">Energía:</span>
                                                        <span className="text-slate-400">{formatPrice(comparisonData.total_energia_pdf, 3)}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-500">Potencia:</span>
                                                        <span className="text-slate-400">{formatPrice(comparisonData.total_potencia_pdf, 3)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center text-primary font-bold border-t border-slate-700/30 pt-1">
                                                <span>Con Imagina (MENSUAL):</span>
                                                <span className="font-mono">{formatPrice(comparisonData.total_imagina_mes, 3)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </section>
                        )}

                        {!comparisonData && !loadingComparison && (
                            <div className="p-3 bg-slate-800/50 border-t border-slate-700 text-center">
                                <p className="text-[9px] text-slate-500 italic">
                                    Suba un PDF para ver la comparativa instantánea
                                </p>
                            </div>
                        )}
                    </div>
                </div>


            </div>
        </AppendixWrapper>
    );
};

export default PriceAppendix;
